<template>
   <div class="container-fluid pact-bg min-vh-100 py-5">
    <div class="card custom-card shadow-lg p-4 w-100">
      <h1 class="text-center fw-bold text-white title-gradient p-3 rounded">
       Companies
      </h1>
      
      <div class="d-flex justify-content-between align-items-center mb-4">
        <AddCompanyForm @company-added="handleCompanyAdded" />
      </div>

      <!-- Companies Table Card -->
      <div class="companies-content">
        <div class="card modern-card">
          <div class="card-header gradient-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0 text-white">
              <i class="fas fa-list me-2"></i>All Companies
              <span class="badge bg-light text-dark ms-2">{{ totalCompanies }}</span>
            </h5>
            <button class="btn btn-light btn-sm shadow-sm" @click="fetchCompanies">
              <i class="fas fa-sync-alt me-1"></i> Refresh
            </button>
          </div>
          
          <div class="card-body p-0">
            <!-- Loader -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status" style="width: 3rem; height: 3rem;">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-3 text-muted fw-bold">Loading companies...</p>
            </div>

            <!-- Companies Table -->
            <div v-else>
              <!-- Pagination Controls Top -->
              <div class="d-flex justify-content-between align-items-center p-3 border-bottom bg-light">
                <div class="d-flex align-items-center">
                  <label class="me-2 text-muted small">Show:</label>
                  <select v-model.number="itemsPerPage" @change="changePage(1)" class="form-select form-select-sm" style="width: auto;">
                    <option :value="10">10</option>
                    <option :value="20">20</option>
                    <option :value="50">50</option>
                    <option :value="100">100</option>
                  </select>
                  <span class="ms-3 text-muted small">
                    Showing {{ startIndex + 1 }} to {{ endIndex }} of {{ totalCompanies }} entries
                  </span>
                </div>
                <div>
                  <input 
                    type="text" 
                    v-model="searchQuery" 
                    class="form-control form-control-sm" 
                    placeholder="Search companies..."
                    style="min-width: 250px;"
                  >
                </div>
              </div>

              <div class="table-responsive" style="max-height: 600px; overflow-y: auto;">
                <table class="table table-hover table-striped mb-0">
                  <thead class="table-dark sticky-top">
                    <tr>
                      <th class="text-nowrap">Company Name</th>
                      <th class="text-nowrap">SOR Company ID</th>
                      <th class="text-nowrap">Primary ID</th>
                      <th class="text-nowrap">Start Date</th>
                      <th class="text-nowrap">End Date</th>
                      <th class="text-nowrap">Legal Name</th>
                      <th class="text-nowrap">Government ID</th>
                      <th class="text-nowrap">Gov ID Type</th>
                      <th class="text-nowrap">Name Control</th>
                      <th class="text-nowrap">SOR</th>
                      <th class="text-nowrap">Likely Primary</th>
                      <th class="text-nowrap">Duplicate ID</th>
                      <th class="text-nowrap">Data Manager</th>
                      <th class="text-nowrap">Status</th>
                      <th class="text-nowrap">Entity ID</th>
                      <th class="text-nowrap">Phone</th>
                      <th class="text-nowrap">Email</th>
                      <th class="text-nowrap">Comments</th>
                      <th class="text-nowrap text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="company in paginatedCompanies" :key="company.id" class="align-middle">
                      <td class="fw-bold text-primary" :title="company.name">{{ truncateText(company.name, 30) }}</td>
                      <td :title="company.sor_company_id">{{ truncateText(company.sor_company_id, 20) }}</td>
                      <td :title="company.primary_id">{{ truncateText(company.primary_id, 20) }}</td>
                      <td class="text-nowrap">{{ formatDate(company.start_date) }}</td>
                      <td class="text-nowrap">{{ formatDate(company.end_date) }}</td>
                      <td :title="company.legal_name">{{ truncateText(company.legal_name, 25) || "N/A" }}</td>
                      <td :title="company.gov_id">{{ truncateText(company.gov_id, 20) || "N/A" }}</td>
                      <td>{{ company.gov_id_type || "N/A" }}</td>
                      <td :title="company.name_control">{{ truncateText(company.name_control, 20) || "N/A" }}</td>
                      <td>{{ company.sor || "N/A" }}</td>
                      <td>{{ company.likely_primary_id || "N/A" }}</td>
                      <td>{{ company.dup_id || "N/A" }}</td>
                      <td :title="company.dm_name">{{ truncateText(company.dm_name, 20) || "N/A" }}</td>
                      <td>
                        <span :class="`badge bg-${getStatusBadge(company.status)} px-3 py-2`">
                          {{ company.status || "N/A" }}
                        </span>
                      </td>
                      <td>{{ company.entity_id || "N/A" }}</td>
                      <td :title="company.corporate_phone">{{ truncateText(company.corporate_phone, 15) || "N/A" }}</td>
                      <td :title="company.corporate_email">{{ truncateText(company.corporate_email, 20) || "N/A" }}</td>
                      <td :title="company.comments">{{ truncateText(company.comments, 30) || "N/A" }}</td>
                      <td class="text-center">
                        <div class="btn-group btn-group-sm shadow-sm" role="group">
                          <button 
                            class="btn btn-warning" 
                            @click="editCompany(company)"
                            data-bs-toggle="tooltip"
                            title="Edit Company"
                          >
                            <i class="fas fa-edit"></i>
                          </button>
                          <button 
                            class="btn btn-danger" 
                            @click="confirmDelete(company)"
                            data-bs-toggle="tooltip"
                            title="Delete Company"
                          >
                            <i class="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <!-- Empty State -->
                <div v-if="filteredCompanies.length === 0 && !loading" class="text-center py-5">
                  <i class="fas fa-building fa-4x text-muted mb-3"></i>
                  <h5 class="text-muted">No companies found</h5>
                  <p class="text-muted">
                    {{ searchQuery ? 'Try adjusting your search criteria.' : 'Click "Add Company" to create your first company.' }}
                  </p>
                </div>
              </div>

              <!-- Pagination Controls Bottom -->
              <div class="d-flex justify-content-between align-items-center p-3 border-top bg-light">
                <div class="text-muted small">
                  Page {{ currentPage }} of {{ totalPages }}
                </div>
                <nav>
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <button class="page-link" @click="changePage(1)" :disabled="currentPage === 1">
                        <i class="fas fa-angle-double-left"></i>
                      </button>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <button class="page-link" @click="changePage(currentPage - 1)" :disabled="currentPage === 1">
                        <i class="fas fa-angle-left"></i>
                      </button>
                    </li>
                    
                    <li 
                      v-for="page in visiblePages" 
                      :key="page" 
                      class="page-item" 
                      :class="{ active: page === currentPage }"
                    >
                      <button class="page-link" @click="changePage(page)">{{ page }}</button>
                    </li>
                    
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <button class="page-link" @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages">
                        <i class="fas fa-angle-right"></i>
                      </button>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <button class="page-link" @click="changePage(totalPages)" :disabled="currentPage === totalPages">
                        <i class="fas fa-angle-double-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Company Modal -->
    <div class="modal fade" id="editCompanyModal" tabindex="-1" data-bs-backdrop="static">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content modern-modal">
          <div class="modal-header gradient-header text-white">
            <h5 class="modal-title">
              <i class="fas fa-edit me-2"></i>Edit Company
            </h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body p-4">
            <form @submit.prevent="updateCompany">
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label fw-bold">
                    Company Name <span class="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="editForm.name"
                    @input="checkFormChanged"
                    required
                    maxlength="255"
                    placeholder="Enter company name"
                  />
                </div>
                <div class="col-md-6">
                  <label class="form-label fw-bold">
                    SOR Company ID <span class="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="editForm.sor_company_id"
                    @input="checkFormChanged"
                    required
                    maxlength="255"
                    placeholder="Enter SOR Company ID"
                  />
                </div>
                <div class="col-md-6">
                  <label class="form-label fw-bold">
                    Primary ID <span class="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="editForm.primary_id"
                    @input="checkFormChanged"
                    required
                    maxlength="255"
                    placeholder="Enter Primary ID"
                  />
                </div>
                <div class="col-md-6">
                  <label class="form-label fw-bold">Status</label>
                  <select v-model="editForm.status" @change="checkFormChanged" class="form-select">
                    <option value="">Select Status</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Pending">Pending</option>
                    <option value="Suspended">Suspended</option>
                  </select>
                </div>
                <div class="col-md-6">
                  <label class="form-label fw-bold">
                    Start Date <span class="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    class="form-control"
                    v-model="editForm.start_date"
                    @input="checkFormChanged"
                    required
                  />
                </div>
                <div class="col-md-6">
                  <label class="form-label fw-bold">
                    End Date <span class="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    class="form-control"
                    v-model="editForm.end_date"
                    @input="checkFormChanged"
                    required
                  />
                </div>
              </div>
              <div class="modal-footer border-0 mt-4">
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                  <i class="fas fa-times me-1"></i> Cancel
                </button>
                <button 
                  type="submit" 
                  class="btn btn-primary"
                  :disabled="updating || !isFormChanged"
                >
                  <span v-if="updating" class="spinner-border spinner-border-sm me-2"></span>
                  <i v-else class="fas fa-save me-1"></i>
                  {{ updating ? "Updating..." : "Update Company" }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal fade" id="deleteCompanyModal" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content modern-modal">
          <div class="modal-header bg-danger text-white">
            <h5 class="modal-title">
              <i class="fas fa-exclamation-triangle me-2"></i>Confirm Delete
            </h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body p-4">
            <div class="text-center mb-3">
              <i class="fas fa-trash-alt fa-3x text-danger mb-3"></i>
              <p class="fs-5">
                Are you sure you want to delete the company<br>
                <strong class="text-primary">"{{ companyToDelete?.name }}"</strong>?
              </p>
              <p class="text-danger fw-bold">
                <i class="fas fa-exclamation-circle me-1"></i>
                This action cannot be undone.
              </p>
            </div>
          </div>
          <div class="modal-footer border-0">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              <i class="fas fa-times me-1"></i> Cancel
            </button>
            <button
              type="button"
              class="btn btn-danger"
              @click="deleteCompany"
              :disabled="deleting"
            >
              <span v-if="deleting" class="spinner-border spinner-border-sm me-2"></span>
              <i v-else class="fas fa-trash me-1"></i>
              {{ deleting ? "Deleting..." : "Delete Company" }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Container -->
    <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 9999;">
      <div
        ref="successToast"
        class="toast align-items-center text-white bg-success border-0"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      >
        <div class="d-flex">
          <div class="toast-body">
            <i class="fas fa-check-circle me-2"></i>
            {{ successMessage }}
          </div>
          <button
            type="button"
            class="btn-close btn-close-white me-2 m-auto"
            data-bs-dismiss="toast"
          ></button>
        </div>
      </div>

      <div
        ref="errorToast"
        class="toast align-items-center text-white bg-danger border-0"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      >
        <div class="d-flex">
          <div class="toast-body">
            <i class="fas fa-exclamation-circle me-2"></i>
            {{ errorMessage }}
          </div>
          <button
            type="button"
            class="btn-close btn-close-white me-2 m-auto"
            data-bs-dismiss="toast"
          ></button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as bootstrap from "bootstrap";
import AddCompanyForm from "./AddCompany.vue";
import { GetCompanies, UpdateCompany, DeleteCompany } from "../End Points/api";

export default {
  name: "CompaniesPage",
  components: {
    AddCompanyForm,
  },
  data() {
    return {
      companies: [],
      loading: false,
      successMessage: "",
      errorMessage: "",
      updating: false,
      deleting: false,
      companyToDelete: null,
      editForm: {
        id: null,
        name: "",
        sor_company_id: "",
        primary_id: "",
        status: "",
        start_date: "",
        end_date: "",
      },
      originalEditForm: {},
      isFormChanged: false,
      currentPage: 1,
      itemsPerPage: 10,
      searchQuery: "",
    };
  },
  computed: {
    filteredCompanies() {
      if (!this.searchQuery) return this.companies;
      
      const query = this.searchQuery.toLowerCase();
      return this.companies.filter(company => 
        company.name?.toLowerCase().includes(query) ||
        company.sor_company_id?.toLowerCase().includes(query) ||
        company.primary_id?.toLowerCase().includes(query) ||
        company.legal_name?.toLowerCase().includes(query) ||
        company.status?.toLowerCase().includes(query)
      );
    },
    totalCompanies() {
      return this.filteredCompanies.length;
    },
    totalPages() {
      return Math.ceil(this.totalCompanies / this.itemsPerPage) || 1;
    },
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    endIndex() {
      return Math.min(this.startIndex + this.itemsPerPage, this.totalCompanies);
    },
    paginatedCompanies() {
      return this.filteredCompanies.slice(this.startIndex, this.endIndex);
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      
      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    }
  },
  mounted() {
    this.fetchCompanies();
  },
  methods: {
    async fetchCompanies() {
      this.loading = true;
      try {
        const response = await GetCompanies();

        if (response && response.data) {
          if (response.data.companies?.results && Array.isArray(response.data.companies.results)) {
            this.companies = response.data.companies.results;
          } else if (Array.isArray(response.data)) {
            this.companies = response.data;
          } else if (response.data.results && Array.isArray(response.data.results)) {
            this.companies = response.data.results;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            this.companies = response.data.data;
          } else if (typeof response.data === "object") {
            this.companies = [response.data];
          } else {
            this.companies = [];
          }
        } else {
          this.companies = [];
        }
      } catch (error) {
        this.showError(error.response?.data?.message || "Failed to load companies.");
        this.companies = [];
      } finally {
        this.loading = false;
      }
    },

    handleCompanyAdded() {
      this.showSuccess("Company added successfully!");
      this.fetchCompanies();
    },

    editCompany(company) {
      this.editForm = {
        id: company.id,
        name: company.name,
        sor_company_id: company.sor_company_id,
        primary_id: company.primary_id,
        status: company.status || "",
        start_date: company.start_date,
        end_date: company.end_date,
      };

      this.originalEditForm = { ...this.editForm };
      this.isFormChanged = false;

      const modal = new bootstrap.Modal(document.getElementById("editCompanyModal"));
      modal.show();
    },

    checkFormChanged() {
      this.isFormChanged = JSON.stringify(this.editForm) !== JSON.stringify(this.originalEditForm);
    },

    async updateCompany() {
      if (!this.validateEditForm()) {
        return;
      }

      this.updating = true;
      try {
        const updateData = {
          name: this.editForm.name,
          sor_company_id: this.editForm.sor_company_id,
          primary_id: this.editForm.primary_id,
          status: this.editForm.status,
          start_date: this.editForm.start_date,
          end_date: this.editForm.end_date,
        };

        await UpdateCompany(this.editForm.id, updateData);

        this.showSuccess("Company updated successfully!");

        const modal = bootstrap.Modal.getInstance(document.getElementById("editCompanyModal"));
        modal.hide();

        this.fetchCompanies();
      } catch (error) {
        this.showError(error.response?.data?.message || "Failed to update company. Please try again.");
      } finally {
        this.updating = false;
      }
    },

    validateEditForm() {
      if (!this.editForm.name.trim()) {
        this.showError("Company Name is required.");
        return false;
      }
      if (!this.editForm.sor_company_id.trim()) {
        this.showError("SOR Company ID is required.");
        return false;
      }
      if (!this.editForm.primary_id.trim()) {
        this.showError("Primary ID is required.");
        return false;
      }
      if (!this.editForm.start_date) {
        this.showError("Start Date is required.");
        return false;
      }
      if (!this.editForm.end_date) {
        this.showError("End Date is required.");
        return false;
      }

      const startDate = new Date(this.editForm.start_date);
      const endDate = new Date(this.editForm.end_date);
      if (endDate <= startDate) {
        this.showError("End date must be after start date.");
        return false;
      }

      return true;
    },

    confirmDelete(company) {
      this.companyToDelete = company;
      const modal = new bootstrap.Modal(document.getElementById("deleteCompanyModal"));
      modal.show();
    },

    async deleteCompany() {
      if (!this.companyToDelete) return;

      this.deleting = true;
      try {
        await DeleteCompany(this.companyToDelete.id);

        this.showSuccess("Company deleted successfully!");

        const modal = bootstrap.Modal.getInstance(document.getElementById("deleteCompanyModal"));
        modal.hide();

        this.fetchCompanies();
      } catch (error) {
        this.showError(error.response?.data?.message || "Failed to delete company. Please try again.");
      } finally {
        this.deleting = false;
        this.companyToDelete = null;
      }
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },

    getStatusBadge(status) {
      switch (status) {
        case "Active":
          return "success";
        case "Inactive":
          return "secondary";
        case "Pending":
          return "warning";
        case "Suspended":
          return "danger";
        default:
          return "dark";
      }
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleDateString();
    },

    truncateText(text, maxLength) {
      if (!text) return "";
      return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
    },

    showSuccess(message) {
      this.successMessage = message;
      const toast = new bootstrap.Toast(this.$refs.successToast, {
        autohide: true,
        delay: 3000
      });
      toast.show();
    },

    showError(message) {
      this.errorMessage = message;
      const toast = new bootstrap.Toast(this.$refs.errorToast, {
        autohide: true,
        delay: 4000
      });
      toast.show();
    }
  },
};
</script>

<style scoped>
.pact-bg {
  background: linear-gradient(135deg, #415971, #4b93a5, #b2a5a5);
  min-height: 100vh;
}

.custom-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 24px;
  border: none;
  
}

.title-gradient {
   background: linear-gradient(135deg, #415971, #4b93a5, #b2a5a5);
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
}

.modern-card {
  border: none;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
}

.gradient-header {
   background: linear-gradient(135deg, #415971, #4b93a5, #b2a5a5);
  border: none;
  padding: 1.25rem 1.5rem;
}

.modern-modal {
  border: none;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.table {
  margin-bottom: 0;
}

.table thead th {
  background: #2c3e50;
  color: white;
  font-weight: 600;
  border: none;
  padding: 1rem;
  font-size: 0.875rem;
}

.table tbody tr {
  transition: all 0.3s ease;
}

.table tbody tr:hover {
  background-color: #f8f9fa;
  transform: translateX(2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.table td {
  padding: 1rem;
  vertical-align: middle;
  font-size: 0.875rem;
}

.btn-group {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  overflow: hidden;
}

.btn {
  transition: all 0.3s ease;
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.badge {
  font-weight: 600;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}

.pagination .page-link {
  border: none;
  color: #667eea;
  font-weight: 600;
  margin: 0 2px;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.pagination .page-link:hover {
  background: #667eea;
  color: white;
  transform: translateY(-2px);
}

.pagination .page-item.active .page-link {
 background: linear-gradient(135deg, #415971, #4b93a5, #b2a5a5);
  border: none;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.pagination .page-item.disabled .page-link {
  opacity: 0.5;
}

.form-control:focus,
.form-select:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

.sticky-top {
  position: sticky;
  top: 0;
  z-index: 10;
}

.toast {
  min-width: 300px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  font-weight: 500;
}

.toast-body {
  padding: 1rem 1.25rem;
  font-size: 0.95rem;
}

/* Scrollbar Styling */
.table-responsive::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-responsive::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.table-responsive::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 10px;
}

.table-responsive::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}

/* Animation for table rows */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.table tbody tr {
  animation: fadeIn 0.3s ease-in-out;
}

/* Modal animations */
.modal.fade .modal-dialog {
  transition: transform 0.3s ease-out;
}

/* Button disabled state */
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Tooltip enhancement */
[data-bs-toggle="tooltip"] {
  cursor: pointer;
}

/* Empty state styling */
.text-center i.fa-building {
  opacity: 0.3;
}

/* Search input enhancement */
input[type="text"].form-control {
  border-radius: 8px;
  border: 2px solid #e9ecef;
  transition: all 0.3s ease;
}

input[type="text"].form-control:focus {
  border-color: #667eea;
  transform: scale(1.02);
}

/* Select dropdown enhancement */
.form-select {
  border-radius: 8px;
  border: 2px solid #e9ecef;
  cursor: pointer;
}

/* Card body padding fix */
.companies-content .card-body {
  padding: 0 !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .table {
    font-size: 0.75rem;
  }
  
  .table td,
  .table th {
    padding: 0.5rem;
  }
  
  .pagination {
    font-size: 0.875rem;
  }
  
  .toast {
    min-width: 250px;
  }
}

/* Print styles */
@media print {
  .btn,
  .pagination,
  .modal,
  .toast-container {
    display: none !important;
  }
  
  .table {
    font-size: 10px;
  }
}
</style>